/**
 * materialRequests.ts — MATERIAL-REQ-01 (Seth 2026-09-25: "Best way for
 * installers and new construction guys to request materials, parts, etc. With
 * an urgency so do they need it asap, ordered, etc." — then "Proceed" on the
 * plan).
 *
 * A crew member types (or dictates) what they need the way they'd say it —
 * "3 3/4 90s, 10 ft 3/8 line set, a disconnect, 2 cans of that foam" — and
 * Pegasus reads it into lines matched to the catalog (the material-request
 * edge function: learned aliases first, then catalog candidates picked by
 * Gemini; "Which one?" when a size is missing; no match = kept as typed).
 * They pick when they need it (Today · Tomorrow AM · By a Date · Just Order
 * It) and send it. The lines ARE job materials (source 'request'): one list
 * per job, each line carrying where it is — requested → being pulled → ready
 * on a shelf, or on order (a Pegasus PO or an ESC PO #) → picked up. The
 * warehouse works them on /warehouse and the job's Warehouse tab; the crew
 * sees every change live on the phone.
 *
 * Competitor frame: ServiceTitan techs request items from the truck/warehouse
 * inventory (a transfer) or the office creates a PO from the job; Jobber has
 * no warehouse flow at all. Nobody reads a crew's own words into catalog
 * items — that's the part that makes it usable from a ladder.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { nextPickupDay } from "@/features/parts/pickupList";
import type { JobMaterial, PullStatus } from "./jobs";

export type Urgency = "today" | "tomorrow_am" | "by_date" | "order";
export const URGENCIES: { v: Urgency; label: string; sub: string }[] = [
  { v: "today", label: "Today", sub: "ASAP — pulled now" },
  { v: "tomorrow_am", label: "Tomorrow AM", sub: "Ready by 7:00" },
  { v: "by_date", label: "By a Date", sub: "Pick the day" },
  { v: "order", label: "Just Order It", sub: "For the job start" },
];
export const URGENCY_LABEL: Record<Urgency, string> = {
  today: "Today · ASAP", tomorrow_am: "Tomorrow AM", by_date: "By a Date", order: "Just Order It",
};

export const PULL_LABEL: Record<PullStatus, string> = {
  requested: "Requested", pulling: "Being pulled", ready: "Ready", ordered: "On order", picked_up: "Picked up", cancelled: "Cancelled",
};

export interface MaterialRequest {
  id: string;
  job_id: string;
  dispatch_id: string | null;
  urgency: Urgency;
  need_by: string | null;
  typed: string | null;
  note: string | null;
  photo_file_id: string | null;
  requested_by: string | null;
  requested_by_empno: string | null;
  requested_by_name: string | null;
  status: "open" | "done" | "cancelled";
  created_at: string;
  updated_at: string;
  lines: JobMaterial[];
}

/** What the reader matched a line to in the catalog. */
export interface CatalogPick { item_id: string; part_number: string | null; description: string | null; in_stock: number | null; learned?: boolean }
export interface ParsedLine {
  typed: string; qty: number; unit: string; name: string;
  question: string; sizes: string[];
  match: CatalogPick | null;
  options: CatalogPick[];
}

/** A PO a request line is on (Pegasus POs only — an ESC PO # rides on the line). */
export interface LinePo { id: string; po_number: string | null; status: string; vendor_code: string | null; expected_date: string | null }

const todayIso = () => new Date().toLocaleDateString("en-CA");

/** When the request is needed by, from the urgency. "Just Order It" = the job's install day. */
export function needByFor(u: Urgency, pickedDate: string | null, installDate: string | null): string | null {
  if (u === "today") return todayIso();
  if (u === "tomorrow_am") return nextPickupDay(todayIso());
  if (u === "by_date") return pickedDate || null;
  return installDate;
}

/** The crew's words → lines matched to the catalog. */
export async function parseMaterialRequest(text: string): Promise<ParsedLine[]> {
  const { data, error } = await supabase.functions.invoke("material-request", { body: { action: "parse", text } });
  if (error) throw new Error("Couldn't read that — check the signal and try again.");
  const d = data as { ok?: boolean; lines?: ParsedLine[]; error?: string } | null;
  if (!d?.ok) throw new Error(d?.error ? `Couldn't read that (${d.error}).` : "Couldn't read that — try again.");
  return (d.lines ?? []).map((l) => ({ ...l, sizes: l.sizes ?? [], options: l.options ?? [] }));
}

/** An SVG QR code (the pick sheet's job label). */
export function useQrSvg(text: string | null) {
  return useQuery({
    queryKey: ["qr_svg", text],
    enabled: !!text,
    staleTime: Infinity,
    queryFn: async (): Promise<string | null> => {
      const { data } = await supabase.functions.invoke("material-request", { body: { action: "qr", text } });
      return (data as { svg?: string } | null)?.svg ?? null;
    },
  });
}

async function withLines(reqs: Omit<MaterialRequest, "lines">[]): Promise<MaterialRequest[]> {
  if (!reqs.length) return [];
  const { data, error } = await supabase.from("pegasus_job_material").select("*")
    .in("request_id", reqs.map((r) => r.id)).order("sort").order("created_at");
  if (error) throw error;
  const by = new Map<string, JobMaterial[]>();
  for (const m of (data ?? []) as JobMaterial[]) {
    const k = m.request_id as string;
    by.set(k, [...(by.get(k) ?? []), m]);
  }
  return reqs.map((r) => ({ ...r, lines: by.get(r.id) ?? [] }));
}

/** Every request on one job, newest first, with its lines. */
export function useJobMaterialRequests(jobId: string | null | undefined) {
  return useQuery({
    queryKey: ["material_requests", jobId ?? null],
    enabled: !!jobId,
    queryFn: async (): Promise<MaterialRequest[]> => {
      const { data, error } = await supabase.from("pegasus_material_request").select("*")
        .eq("job_id", jobId!).order("created_at", { ascending: false });
      if (error) throw error;
      return withLines((data ?? []) as Omit<MaterialRequest, "lines">[]);
    },
  });
}

export interface QueueJob {
  id: string; title: string | null; custno: string; locno: string | null; customer: string; address: string | null;
  installDate: string | null; installWindow: string | null; crewEmpno: string | null; nc: boolean;
}
export interface RequestQueue { requests: MaterialRequest[]; jobs: Map<string, QueueJob>; pos: Map<string, LinePo>; cells: Map<string, string> }

function cleanName(fullname: string | null): string | null {
  if (!fullname) return null;
  let s = fullname.replace(/\s+\d{4,}\s*$/, "").trim();
  const m = s.match(/^([^,]+),\s*(.+)$/);
  if (m) s = `${m[2]} ${m[1]}`.trim();
  return s || null;
}

/** The warehouse's queue: every open request (optionally one job's), with the job, the install day + crew, and the POs. */
export function useMaterialRequestQueue(jobId?: string | null) {
  return useQuery({
    queryKey: ["material_requests_open", jobId ?? "all"],
    refetchInterval: 60 * 1000, // the warehouse leaves this open
    queryFn: async (): Promise<RequestQueue> => {
      let q = supabase.from("pegasus_material_request").select("*").order("need_by", { ascending: true, nullsFirst: false }).order("created_at");
      q = jobId ? q.eq("job_id", jobId) : q.eq("status", "open");
      const { data, error } = await q;
      if (error) throw error;
      const requests = await withLines((data ?? []) as Omit<MaterialRequest, "lines">[]);
      const jobIds = [...new Set(requests.map((r) => r.job_id))];
      const jobs = new Map<string, QueueJob>();
      const pos = new Map<string, LinePo>();
      const cells = new Map<string, string>();
      const empnos = [...new Set(requests.map((r) => r.requested_by_empno).filter(Boolean) as string[])];
      if (empnos.length) {
        const { data: tp } = await supabase.from("tech_profile").select("empno, cellphone").in("empno", empnos);
        for (const t of (tp ?? []) as { empno: string; cellphone: string | null }[]) if (t.cellphone) cells.set(t.empno, t.cellphone);
      }
      if (jobIds.length) {
        const [{ data: js }, { data: vs }] = await Promise.all([
          supabase.from("pegasus_job").select("id, title, custno, locno, site_address, site_city, job_type").in("id", jobIds),
          supabase.from("pegasus_dispatch").select("job_id, scheduled_date, scheduled_window, assigned_empno, visit_role")
            .in("job_id", jobIds).is("deleted_at", null).neq("status", "cancelled"),
        ]);
        const raw = (js ?? []) as { id: string; title: string | null; custno: string; locno: string | null; site_address: string | null; site_city: string | null; job_type: string | null }[];
        const custnos = [...new Set(raw.map((j) => j.custno))];
        const [{ data: cs }, { data: ls }] = await Promise.all([
          supabase.from("mirror_customer").select("custno, fullname").in("custno", custnos),
          supabase.from("mirror_location").select("custno, locno, add1, city").in("custno", custnos),
        ]);
        const names = new Map(((cs ?? []) as { custno: string; fullname: string | null }[]).map((c) => [c.custno, cleanName(c.fullname)]));
        const locs = new Map(((ls ?? []) as { custno: string; locno: string | null; add1: string | null; city: string | null }[]).map((l) => [`${l.custno}|${l.locno}`, l]));
        const today = todayIso();
        for (const j of raw) {
          const visits = ((vs ?? []) as { job_id: string; scheduled_date: string | null; scheduled_window: string | null; assigned_empno: string | null }[])
            .filter((v) => v.job_id === j.id && v.scheduled_date)
            .sort((a, b) => (a.scheduled_date! < b.scheduled_date! ? -1 : 1));
          const next = visits.find((v) => v.scheduled_date! >= today) ?? visits[visits.length - 1] ?? null;
          const loc = locs.get(`${j.custno}|${j.locno}`);
          jobs.set(j.id, {
            id: j.id, title: j.title, custno: j.custno, locno: j.locno,
            customer: names.get(j.custno) ?? j.custno,
            address: j.site_address ? [j.site_address, j.site_city].filter(Boolean).join(", ") : loc ? [loc.add1, loc.city].filter(Boolean).join(", ") : null,
            installDate: next?.scheduled_date ?? null, installWindow: next?.scheduled_window ?? null, crewEmpno: next?.assigned_empno ?? null,
            nc: j.job_type === "new_construction" || j.job_type === "nc",
          });
        }
        const poIds = [...new Set(requests.flatMap((r) => r.lines.map((l) => l.po_id)).filter(Boolean) as string[])];
        if (poIds.length) {
          const { data: ps } = await supabase.from("pegasus_po").select("id, po_number, status, vendor_code, expected_date").in("id", poIds);
          for (const p of (ps ?? []) as LinePo[]) pos.set(p.id, p);
        }
      }
      return { requests, jobs, pos, cells };
    },
  });
}

function useInvalidateRequests() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["material_requests"] });
    void qc.invalidateQueries({ queryKey: ["material_requests_open"] });
    void qc.invalidateQueries({ queryKey: ["job_materials"] });
    void qc.invalidateQueries({ queryKey: ["jobs"] });
  };
}

async function me(): Promise<string | null> {
  const { data } = await supabase.auth.getUser();
  return data?.user?.email ?? null;
}

/** Send a request (the lines the crew confirmed). */
export function useCreateMaterialRequest() {
  const inval = useInvalidateRequests();
  return useMutation({
    mutationFn: async (v: {
      jobId: string; dispatchId: string | null; urgency: Urgency; needBy: string | null; typed: string; note: string;
      lines: { description: string; qty: number; unit: string | null; item_id: string | null; typed: string; learn: boolean }[];
      empno: string | null; name: string | null; photoFileId?: string | null;
    }): Promise<string> => {
      const { data, error } = await supabase.rpc("create_material_request", {
        p_job_id: v.jobId, p_dispatch_id: v.dispatchId, p_urgency: v.urgency, p_need_by: v.needBy,
        p_typed: v.typed, p_note: v.note, p_lines: v.lines, p_empno: v.empno, p_name: v.name, p_photo_file_id: v.photoFileId ?? null,
      });
      if (error) throw new Error(/NO_LINES/.test(error.message) ? "Add at least one thing to ask for." : "Didn't send — check the signal and try again.");
      return data as string;
    },
    onSuccess: inval,
  });
}

/** Move lines along: pulling, ready (on a shelf), picked up, cancelled, back to requested. */
export function useSetLineStatus() {
  const inval = useInvalidateRequests();
  return useMutation({
    mutationFn: async (v: { ids: string[]; status: PullStatus; shelf?: string | null }) => {
      if (!v.ids.length) return;
      const patch: Record<string, unknown> = { pull_status: v.status, status_at: new Date().toISOString(), status_by: await me() };
      if (v.shelf !== undefined) patch.shelf = (v.shelf ?? "").trim() || null;
      // Cancelled costs nothing and takes nothing off stock at close.
      if (v.status === "cancelled") patch.qty_actual = 0;
      const { error } = await supabase.from("pegasus_job_material").update(patch).in("id", v.ids);
      if (error) throw error;
    },
    onSuccess: inval,
  });
}

/** Fix a line the reader got wrong (the warehouse's edit also teaches the alias). */
export function useFixRequestLine() {
  const inval = useInvalidateRequests();
  return useMutation({
    mutationFn: async (v: { line: JobMaterial; description: string; qty: number; item: { id: string } | null }) => {
      let cost = v.line.unit_cost;
      if (v.item) {
        const { data: it } = await supabase.from("item").select("average_cost, last_purchase").eq("id", v.item.id).maybeSingle();
        const c = it as { average_cost: number | null; last_purchase: number | null } | null;
        cost = c?.average_cost ?? c?.last_purchase ?? null;
      }
      const { error } = await supabase.from("pegasus_job_material").update({
        description: v.description.trim() || v.line.description, qty: v.qty,
        item_id: v.item?.id ?? v.line.item_id ?? null, unit_cost: v.line.po_id ? null : cost, // on a PO, the PO carries the cost
        changed_at: new Date().toISOString(), changed_by: await me(),
      }).eq("id", v.line.id);
      if (error) throw error;
      if (v.item && v.line.typed) {
        const phrase = v.line.typed.trim().replace(/\s+/g, " ").toLowerCase();
        await supabase.from("pegasus_item_alias").upsert({ phrase, item_id: v.item.id, created_by: await me(), updated_at: new Date().toISOString() }, { onConflict: "phrase" });
      }
    },
    onSuccess: inval,
  });
}

/** Order It — the ESC PO number (ESC still cuts the POs today). */
export function useOrderInEsc() {
  const inval = useInvalidateRequests();
  return useMutation({
    mutationFn: async (v: { ids: string[]; escPo: string; due: string | null }) => {
      const { error } = await supabase.from("pegasus_job_material").update({
        pull_status: "ordered", esc_po: v.escPo.trim(), po_id: null, due_date: v.due || null,
        status_at: new Date().toISOString(), status_by: await me(),
      }).in("id", v.ids);
      if (error) throw error;
    },
    onSuccess: inval,
  });
}

/** Order It — a Pegasus PO on the job (a draft the buyer places), the lines ride on it; receiving the PO makes them Ready. */
export function useOrderOnPegasusPo() {
  const inval = useInvalidateRequests();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      job: { id: string; custno: string; locno: string | null }; vendorCode: string; due: string | null;
      lines: JobMaterial[]; memo: string;
    }): Promise<{ id: string; po_number: string | null }> => {
      const email = await me();
      const itemIds = [...new Set(v.lines.map((l) => l.item_id).filter(Boolean) as string[])];
      const parts = new Map<string, string | null>();
      if (itemIds.length) {
        const { data } = await supabase.from("item").select("id, part_number, vendor_part_number").in("id", itemIds);
        for (const i of (data ?? []) as { id: string; part_number: string | null; vendor_part_number: string | null }[]) parts.set(i.id, i.vendor_part_number || i.part_number);
      }
      const { data: po, error } = await supabase.from("pegasus_po").insert({
        vendor_code: v.vendorCode, status: "draft", job_id: v.job.id, custno: v.job.custno, locno: v.job.locno,
        date_requested: v.due, expected_date: v.due, memo: v.memo, buyer: email, created_by: email,
      }).select("id, po_number").single();
      if (error) throw error;
      const rows = v.lines.map((l, i) => ({
        po_id: (po as { id: string }).id, part_number: l.item_id ? parts.get(l.item_id) ?? null : null,
        description: l.description, qty: Number(l.qty) || 1, unit_cost: l.unit_cost ?? null, sort: i,
      }));
      const { error: le } = await supabase.from("pegasus_po_line").insert(rows);
      if (le) throw le;
      // The PO line carries the cost now (Job Costing → Materials (POs)); the
      // request line keeps tracking where it is — so the cost counts once.
      const { error: ue } = await supabase.from("pegasus_job_material").update({
        unit_cost: null,
        pull_status: "ordered", po_id: (po as { id: string }).id, esc_po: null, due_date: v.due,
        status_at: new Date().toISOString(), status_by: email,
      }).in("id", v.lines.map((l) => l.id));
      if (ue) throw ue;
      return po as { id: string; po_number: string | null };
    },
    onSuccess: () => { inval(); void qc.invalidateQueries({ queryKey: ["native_pos"] }); },
  });
}

/** Plain words for where a line is, for the crew and the warehouse. */
export function lineStatusText(l: JobMaterial, po?: LinePo | null): string {
  const s = (l.pull_status ?? "requested") as PullStatus;
  const day = (iso: string | null | undefined) => iso ? new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : null;
  if (s === "ready") return `Ready${l.shelf ? ` · ${l.shelf}` : ""}`;
  if (s === "ordered") {
    const on = po ? `PO ${po.po_number ?? ""}${po.status === "draft" ? " (not placed yet)" : ""}` : l.esc_po ? `ESC PO ${l.esc_po}` : null;
    const due = day(l.due_date ?? po?.expected_date ?? null);
    return ["On order", on, due ? `due ${due}` : null].filter(Boolean).join(" · ");
  }
  return PULL_LABEL[s];
}

/** Open = the warehouse still owes something. */
export const lineIsOpen = (l: JobMaterial) => l.pull_status === "requested" || l.pull_status === "pulling" || l.pull_status === "ordered";
